/**
 * Approval lease API (Phase 3 — claim-first + delegation/escalation).
 *
 * Endpoints mirror ``one_app/apps/stepflow/backend/api/rest_handlers.py``:
 *   GET    /networks/{id}/pools/{poolId}/approvals            roster of live leases
 *   POST   /networks/{id}/pools/{poolId}/approvals/{taskId}/claim
 *   POST   /networks/{id}/pools/{poolId}/approvals/{taskId}/delegate
 *   POST   /networks/{id}/pools/{poolId}/approvals/{taskId}/escalate
 *
 * These endpoints are orthogonal to the existing ``POST /approval`` resolver
 * (it now rejects with 409 ClaimRequired when no live claim is held). Any
 * pool member can claim; delegate requires the caller to already hold the
 * claim; escalate is an SOS that anyone (including workspace admins) can
 * trigger — it clears the claim so the next tier can pick it up.
 */
import { getRestApiBearerToken } from './apiToken';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

function encodePath(s) {
  return encodeURIComponent(String(s || '').trim());
}

async function authedFetch(path, init = {}) {
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data.error || data.message || `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

function base(networkId, poolId) {
  return `/networks/${encodePath(networkId)}/pools/${encodePath(poolId)}/approvals`;
}

/**
 * Snapshot live approval leases for a pool. Joining supervisors call this
 * on mount to hydrate their panel without waiting for the next AppSync
 * ``APPROVAL_CLAIMED`` beacon.
 *
 * @param {string} networkId
 * @param {string} poolId
 * @param {{ includeResolved?: boolean }} [opts]
 * @returns {Promise<{ approvals: ApprovalLease[] }>}
 */
export async function listApprovals(networkId, poolId, opts = {}) {
  const qs = opts.includeResolved ? '?includeResolved=true' : '';
  return authedFetch(`${base(networkId, poolId)}${qs}`);
}

/**
 * Claim or refresh an approval lease. Returns 409 + current lease when a
 * peer already holds it — the UI renders "Jane is reviewing…" from the
 * embedded lease payload without a follow-up GET.
 *
 * @throws {Error & { status: 409, data: { lease: ApprovalLease } }}
 */
export async function claimApproval(networkId, poolId, taskId, ttlSeconds = 300) {
  return authedFetch(`${base(networkId, poolId)}/${encodePath(taskId)}/claim`, {
    method: 'POST',
    body: JSON.stringify({ ttlSeconds }),
  });
}

/**
 * Hand a claim to another pool member. ``toSub`` is the target's Cognito
 * sub; ``toEmail`` is decorative (embedded in the event payload). The
 * target must already be a pool member — the backend 400s otherwise.
 */
export async function delegateApproval(networkId, poolId, taskId, { toSub, toEmail, reason, ttlSeconds } = {}) {
  if (!toSub) throw new Error('delegateApproval: toSub is required');
  return authedFetch(`${base(networkId, poolId)}/${encodePath(taskId)}/delegate`, {
    method: 'POST',
    body: JSON.stringify({ toSub, toEmail, reason, ttlSeconds }),
  });
}

/**
 * Escalate to the next tier. Clears the current claim and flips the
 * lease to ESCALATED; the Step Function stays paused until the target
 * (or any workspace admin) claims + resolves.
 */
export async function escalateApproval(networkId, poolId, taskId, { escalationTarget, reason } = {}) {
  return authedFetch(`${base(networkId, poolId)}/${encodePath(taskId)}/escalate`, {
    method: 'POST',
    body: JSON.stringify({ escalationTarget, reason }),
  });
}

/**
 * Tenant-wide Approval Overwatch — workspace OWNER/ADMIN only. Lists
 * escalated (and optionally active) approval leases across every pool
 * in the tenant so admins have a single drain-the-queue surface when
 * the auto-escalator fires across many rooms at once.
 *
 * Backend: ``GET /tenants/{tenantId}/approvals`` → handler
 * ``list_tenant_approvals_handler`` in rest_handlers.py. Route rejects
 * cross-tenant reads even for admins (JWT tenant must match path).
 *
 * @param {string} tenantId
 * @param {{ includeActive?: boolean, limit?: number }} [opts]
 * @returns {Promise<{ approvals: ApprovalLease[], count: number, escalatedCount: number }>}
 */
export async function listTenantApprovals(tenantId, opts = {}) {
  const qs = new URLSearchParams();
  if (opts.includeActive) qs.set('includeActive', 'true');
  if (opts.limit) qs.set('limit', String(opts.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return authedFetch(`/tenants/${encodePath(tenantId)}/approvals${suffix}`);
}

/**
 * @typedef {Object} ApprovalLease
 * @property {string} taskId
 * @property {string} networkId
 * @property {string} poolId
 * @property {"OPEN"|"CLAIMED"|"DELEGATED"|"ESCALATED"|"RESOLVED"} status
 * @property {string} [claimerSub]
 * @property {string} [claimerEmail]
 * @property {string} [claimedAt]
 * @property {number} [expiresAt]   epoch seconds
 * @property {string} [createdAt]
 * @property {string} [updatedAt]
 * @property {string} [delegatedFrom]
 * @property {string} [delegatedTo]
 * @property {string} [delegateReason]
 * @property {string} [escalatedBy]
 * @property {string} [escalatedAt]
 * @property {string} [escalationTarget]
 * @property {string} [escalationReason]
 * @property {string} [resolvedBy]
 * @property {string} [resolvedAt]
 * @property {"APPROVE"|"DENY"|"MODIFY"} [decision]
 * @property {string} [feedback]
 * @property {string} [agentName]
 * @property {string} [requesterSub]
 * @property {boolean} live
 */
